import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { FormContainer, TextFieldElement, useForm } from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { BrandInput } from '@barboxx/admin-components';
import { CREATE_PRODUCT_LINE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductLine } from '@barboxx/models';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateProductLineDialog = ({
    onClose,
    onSuccess,
    open,
}: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<ProductLine.CreateRequest>({
        resolver: yupResolver(CREATE_PRODUCT_LINE_SCHEMA),
        defaultValues: {},
    });

    const mutateCreateProductLine = useMutation({
        mutationKey: ['productLine'],
        mutationFn: (data: ProductLine.CreateRequest) =>
            Api.Products.ProductLines.postProductLine(data),
    });

    const onSubmit = async (values: ProductLine.CreateRequest) => {
        return mutateCreateProductLine
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                handleCancelCreate();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating productLine!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleCancelCreate}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Product Line
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <BrandInput />
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
